import type { m365ErrorsTr, m365UserTr } from './m365-user.tr.js';

/** English twin of m365-user.tr.ts; the same keys. */
export const m365UserEn: typeof m365UserTr = {
  name: 'Microsoft 365',
  products: {
    label: 'Products',
    mail: 'Outlook mail',
    calendar: 'Outlook calendar',
    onedrive: 'OneDrive',
    sharepoint: 'SharePoint',
    ready: 'connected',
    off: 'off',
  },
  connectors: {
    chip: 'Built in',
    section: 'Built-in connections',
    sectionHint: 'Comes with Oppy; if your company turned it on, you connect in one click',
    empty: 'Your company has not turned on a built-in connection yet.',
  },
  card: {
    description: 'Ask about your mail and your calendar.',
    descriptionWrite: 'Ask about your mail and your calendar, have replies written.',
    descriptionDocs: 'Ask about your mail and your calendar; bring documents from OneDrive and SharePoint.',
    descriptionAll: 'Ask about your mail and your calendar, have replies written; bring documents from OneDrive and SharePoint.',
    state: {
      connected: 'Connected',
      not_connected: 'Connect your account',
      needs_reconnect: 'Reconnect',
      consent_required: 'Waiting for admin consent',
      unavailable: 'Unavailable',
      off_firm: 'Your company turned it off',
      not_allowed: 'Not open to your team',
      off_team: 'Off for your team',
      not_configured: 'Not set up',
    },
    connect: 'Connect my account',
    reconnect: 'Reconnect',
    retry: 'Try again',
    toggle: 'Turn Microsoft 365 on or off',
    menu: 'Actions for Microsoft 365',
    details: 'Details',
    remove: 'Remove the connection',
    removeTitle: 'Remove your Microsoft connection?',
    removeBody:
      'Your key in Oppy is deleted; Oppy can no longer look at your mail and calendar. What Oppy already read stays in your conversations. Your Microsoft account and your mail stay as they are. You can connect again at any time.',
    body: {
      reconnect: 'Your Microsoft account no longer allows Oppy. It goes on once you reconnect.',
      consent: "Microsoft needs your company admin's consent for these permissions. Let your IT team know.",
      unavailable: 'The server cannot reach Microsoft right now. Try again in a moment; if it goes on, tell your admin.',
      unavailableAdmin: 'The server cannot reach Microsoft right now. Test the connection in <go>Administration › Integrations</go>.',
      switchedOff: 'You turned it off: Oppy does not look at your mail and calendar. Your account stays connected.',
    },
    off: {
      offFirm: 'Your company turned Microsoft 365 off. Once it is on again, you can connect here.',
      notAllowed: 'Your company opened Microsoft 365 to chosen teams only; your team is not one of them.',
      offTeam: 'Your team admin turned Microsoft 365 off for your team.',
      offTeamNamed: 'The admin of the {{team}} team turned Microsoft 365 off for the team.',
      notConfigured: 'Microsoft 365 is not set up in your company.',
    },
    setup: 'Set it up in <go>Administration › Integrations</go>',
    setupNote: 'Until it is set up, only admins see this card.',
    foot: {
      open: 'Your company turned it on · everyone connects with their own Microsoft account · Oppy never sees your password',
      connected: 'Connected as {{email}} · Oppy only reads, it sends nothing',
      account: '{{email}} · Oppy only reads, it sends nothing',
      readOnly: 'Oppy only reads, it sends nothing',
      connectedWrite: 'Connected as {{email}} · Oppy sends nothing without your approval',
      accountWrite: '{{email}} · Oppy sends nothing without your approval',
      readOnlyWrite: 'Oppy sends nothing without your approval',
    },
    everywhere: 'The Microsoft 365 switch applies everywhere; it does not change by workspace.',
  },
  tools: {
    note: 'All of them only read; Oppy uses them without asking and sends nothing.',
    readOnly: 'Read-only',
    off: 'Your company turned it off',
    mail_ara: { title: 'Search mail', text: 'Searches your inbox and the mail you sent.' },
    mail_oku: { title: 'Read mail', text: 'Reads the text of a mail.' },
    eki_oku: { title: 'Read attachment', text: 'Reads an attachment of a mail when you ask (PDF, Word, Excel, PowerPoint).' },
    takvime_bak: { title: 'Look at the calendar', text: 'Lists the meetings of a day or a few days.' },
    toplanti_ayrintisi: { title: 'Meeting details', text: 'Reads the attendees and the note of a meeting.' },
    uygun_saat_bul: { title: 'Find a free time', text: "Looks only at free/busy in people's calendars." },
    writeNote: 'Each one opens an approval card; nothing goes out before you press Send or Create.',
    approval: 'With your approval each time',
    grant: 'Permission needed',
    yanit_taslagi: { title: 'Reply draft', text: 'Prepares a reply to a mail you name and shows it on a card.' },
    taslagi_gonder: { title: 'Send a draft', text: 'Shows a draft in Outlook on a card and sends it with your approval.' },
    mail_gonder: { title: 'Send a new mail', text: 'Prepares a new mail to people you typed or named.' },
    toplanti_olustur: { title: 'Create a meeting', text: 'Shows the meeting on a card; creates it and sends the invitations with your approval.' },
  },
  consent: {
    request: 'Tell the admin',
    sent: 'Your admin was told.',
    already: 'Your admin was told a short while ago.',
  },
  dialog: {
    title: 'Connect your Microsoft account',
    subtitle: 'Sign in with your company account (e.g. first.last@yourcompany.com)',
    can: 'Oppy will be able to',
    canItems: {
      find: 'Find, read and summarize your mail',
      calendar: 'Read the meetings in your calendar',
      attachments: 'Read mail attachments when you ask',
      schedule: 'Find a free time for a meeting (free/busy only)',
      documents: 'Bring the documents you pick from OneDrive and SharePoint',
      onedrive: 'Bring the documents you pick from OneDrive',
      sharepoint: 'Bring the documents you pick from SharePoint',
      sendAndMeet: 'Send mail and create meetings with your approval',
      send: 'Send mail with your approval',
      meetings: 'Create meetings with your approval',
    },
    cannot: 'Oppy does not',
    cannotItems: {
      send: 'Send mail or set up meetings',
      sendApproval: 'Send mail or set up meetings without your approval',
      sendApprovalMail: 'Send mail without your approval, or set up meetings',
      sendApprovalMeeting: 'Send mail, or set up meetings without your approval',
      delete: 'Delete any mail or file',
      password: 'See or keep your password',
      others: "Look at anyone else's mail",
    },
    upgrade: {
      title: 'Give your Microsoft account a new permission',
      subtitle: 'Microsoft asks you only for the new permission; your reading permissions stay as they are.',
      can: 'From now on Oppy will also be able to',
    },
    noteRedirect:
      "Microsoft's sign-in page opens. After signing in you come back here on your own. You can remove the connection at any time in <b>Settings › Account › Linked accounts</b>.",
    noteDevice: "On Microsoft's page you type the code you will see here. You can remove the connection at any time in <b>Settings › Account › Linked accounts</b>.",
    continueRedirect: 'Continue at Microsoft',
    continueDevice: 'Continue',
    badUrl: "Microsoft's sign-in page could not be opened. Tell your admin.",
    device: {
      intro: "Type this code on Microsoft's page:",
      steps: {
        open: "Open the page in a new tab with \"Open Microsoft's page\".",
        enter: 'Paste the code, sign in with your company account and allow it.',
        done: 'After you sign in, this window closes on its own.',
      },
      open: "Open Microsoft's page",
      waiting: 'Waiting for your sign-in…',
      until: 'The code works until {{time}}',
      expired: 'The code expired. Get a new code and try again.',
      declined: 'You cancelled the sign-in at Microsoft. Try again if you like.',
      newCode: 'Get a new code',
    },
  },
  result: {
    connected: 'Your Microsoft account is connected. You can now ask about your mail and your calendar.',
    failed: 'Your Microsoft account could not be connected.',
  },
  reasons: {
    state_invalid: 'The sign-in took too long or was started in another window. Try again.',
    access_denied: 'You cancelled the sign-in at Microsoft or did not allow it. Try again if you like.',
    consent_required: "Microsoft needs your company admin's consent for these permissions. Let your IT team know.",
    personal_account: 'A personal Microsoft account (outlook.com, hotmail.com) cannot be connected. Sign in with your company account.',
    other_tenant: "This account is not one of your company's Microsoft accounts. Sign in with your company account.",
    guest_account: 'A guest account cannot be connected. Sign in with your own account at the company.',
    wrong_account: 'This Microsoft account does not match your Oppy account. Sign in with the Microsoft account that has the e-mail address you sign in to Oppy with.',
    id_token_invalid: "Microsoft's answer could not be verified. Try again; if it goes on, tell your admin.",
    exchange_failed: 'The Microsoft sign-in could not be finished. Try again; if it goes on, tell your admin.',
    not_available: 'You cannot use Microsoft 365 right now; your company or team turned it off.',
    unavailable: 'The server cannot reach Microsoft right now. Try again in a moment.',
    unknown: 'Something went wrong. Try again; if it goes on, tell your admin.',
  },
  linked: {
    title: 'Linked accounts',
    account: '{{email}} · connected: {{connected}}',
    lastUsed: 'last used: {{used}}',
    notConnected: 'Not connected. Once connected, Oppy can read your mail and calendar.',
    read: 'Reading on',
    off: 'Off',
    rows: { mail: 'Outlook mail', calendar: 'Outlook calendar', mailSend: 'Sending mail', calendarWrite: 'Creating meetings', onedrive: 'OneDrive', sharepoint: 'SharePoint' },
    values: { read: 'Reading on', approval: 'With your approval each time', on: 'On', grant: 'Permission needed', off: 'Off' },
    grantAction: 'Reconnect',
    grantLabel: 'Reconnect for {{row}}',
    removeNote: 'If you remove it, your key in Oppy is deleted. What Oppy already read stays in your conversations.',
    removeNoteDocs: 'If you remove it, your key in Oppy is deleted; the documents you brought stay in Documents.',
    none: 'You have no other linked account. Other connections are on the <go>Connections</go> page.',
    others: 'Other connections are on the <go>Connections</go> page.',
  },
  notice: {
    connected: { title: 'Your Microsoft account is connected', text: 'Oppy can now answer the question it could not answer a moment ago.' },
    notConnected: { title: 'Connect your Microsoft account for this', text: 'Once you connect, Oppy answers your question on its own.' },
    reconnect: { title: 'Your Microsoft connection needs to be renewed', text: 'Your Microsoft account no longer allows Oppy. It goes on once you reconnect.' },
    consent: { title: "Waiting for your company's consent", text: "Microsoft needs your company admin's consent for these permissions. Let your IT team know." },
    scopes: { title: 'A new permission is needed', text: 'Connect your Microsoft account once more for this; Microsoft will ask you for the new permission.' },
    unavailable: { title: 'Microsoft 365 is unavailable right now', text: 'The server cannot connect to Microsoft right now. Try again in a moment; if it goes on, tell your admin.' },
    unavailableAdmin: { title: 'Microsoft 365 is unavailable right now', text: 'The server cannot connect to Microsoft right now. If it goes on: <go>Administration › Integrations</go> › Test the connection.' },
    notAvailable: { title: 'Microsoft 365 is off for you', text: 'Your company or team turned it off. Once it is on again, you can ask again.' },
    offFirm: { title: 'Microsoft 365 is off in your company', text: 'Your company turned it off. Once it is on again, you can ask again.' },
    notAllowed: { title: 'Microsoft 365 is not open to your team', text: 'Your company opened it to chosen teams only.' },
    offTeam: { title: 'Microsoft 365 is off for your team', text: 'Your team admin turned it off.' },
    offTeamNamed: { title: 'Microsoft 365 is off for your team', text: 'The admin of the {{team}} team turned it off.' },
    notConfigured: { title: 'Microsoft 365 is not set up in your company', text: 'Ask your company admin to set it up.' },
    switchedOff: { title: 'You turned Microsoft 365 off', text: 'You can turn it on again on the Connections page.' },
    mailOff: { title: 'Reading Outlook mail is off', text: 'Your company turned off Oppy reading your mail.' },
    calendarOff: { title: 'Reading the Outlook calendar is off', text: 'Your company turned off Oppy reading your calendar.' },
    continue: 'Continue',
    connectors: 'Open the Connections page',
  },
  chip: { connected: 'Microsoft 365 connected', hint: 'Connected as {{email}}' },
};

export const m365ErrorsEn: typeof m365ErrorsTr = {
  microsoft_not_configured: { title: 'Microsoft 365 is not set up in your company yet.', action: 'Ask your company admin to set it up.' },
  microsoft_not_available: { title: 'You cannot use Microsoft 365 right now; your company or team turned it off.', action: 'Ask your team admin or company admin.' },
  microsoft_not_connected: { title: 'Your Microsoft account is not connected.', action: 'Connect your account on the Microsoft 365 card on the Connections page.' },
  microsoft_reconnect: { title: 'Your Microsoft connection needs to be renewed.', action: 'Use "Reconnect" on the Connections page.' },
  microsoft_consent_required: { title: "Microsoft needs your company admin's consent for these permissions.", action: 'Let your IT team know.' },
  microsoft_unavailable: { title: 'The server cannot reach Microsoft right now.', action: 'Try again in a moment; if it goes on, tell your admin.' },
  microsoft_secret_required: { title: 'Direct sign-in needs a client secret.', action: 'Create a new client secret in the Microsoft registration and paste it here.' },
  microsoft_scopes_required: { title: 'A new permission is needed.', action: 'Connect your Microsoft account once more; Microsoft will ask you for the new permission.' },
  microsoft_card_required: { title: "This approval card can't be sent here.", action: 'Send the card in the conversation on the web, or cancel it.' },
  microsoft_card_changed: { title: 'This card is no longer as it was shown.', action: 'Refresh the conversation and look at the card as it is now.' },
  recipient_not_allowed: { title: 'In trial mode, mail can only go to the allowed addresses.', action: 'Change the recipient or ask your admin.' },
  acknowledgement_required: { title: 'Confirm the warning before you share.', action: 'Tick "I understand" and try again.' },
};
